import React, { useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import DailyTvPoster from "../HadithPage/DailyTvPoster";
import { Badge, Button, Icon, useToast } from "stories/Admin";
import {
  DailyCard,
  DailyHistory,
  DailySkeleton,
  DismissibleError,
  formatShortDate,
} from "../HadithPage/DailyView";
import styles from "../HadithPage/HadithPage.module.scss";
import { RenderTop } from "./QuranTextAdminTab";

interface QuranSource {
  surah_name: string;
  surah_number: number;
  ayah_number: number;
  reference_note: string;
}

interface QuranVerse {
  arabic: string;
  transliteration?: string;
  norwegian: string;
  source: QuranSource;
}

interface AIQuran {
  id: number;
  date_iso: string;
  topic_tags: string[];
  verse: QuranVerse;
  explanation_norwegian: string;
  action_points: string[];
  fiqh_controversy_risk: string;
  confidence: number;
  cached: boolean;
  cacheTimestamp: string;
}

interface QuranResponse {
  success: boolean;
  data: AIQuran;
  error?: string;
  generatedAt: string;
}

const AiQuranTab: React.FC<{ top: RenderTop }> = ({ top }) => {
  const { execute } = useFetch();
  const toast = useToast();
  const [allVerses, setAllVerses] = useState<AIQuran[]>([]);
  const [todayQuran, setTodayQuran] = useState<AIQuran | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAllVerses = async () => {
    try {
      const result = (await execute(
        "/api/v1/ai-quran/admin/all",
        "GET",
      )) as QuranResponse[];
      if (result) {
        const verses = result
          .filter((response) => response && response.data)
          .map((response) => response.data);
        setAllVerses(verses);
      }
    } catch (err) {
      console.error("Error fetching all Quran verses:", err);
    }
  };

  const fetchTodayQuran = async () => {
    try {
      const result = (await execute(
        "/api/v1/ai-quran",
        "GET",
      )) as QuranResponse;
      if (result && result.success && result.data) {
        setTodayQuran(result.data);
      }
    } catch (err) {
      console.error("Error fetching today's Quran verse:", err);
      setError("Kunne ikke hente dagens vers.");
    }
  };

  const regenerateTodayQuran = async () => {
    setRegenerating(true);
    setError(null);
    try {
      const result = (await execute(
        "/api/v1/ai-quran/regenerate",
        "POST",
      )) as QuranResponse;
      if (result && result.success && result.data) {
        setTodayQuran(result.data);
        await fetchAllVerses();
        toast.success("Dagens vers er generert på nytt.");
      } else if (result && result.error) {
        setError(result.error);
      }
    } catch (err) {
      console.error("Error regenerating Quran verse:", err);
      setError("Kunne ikke generere verset på nytt. Prøv igjen senere.");
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchTodayQuran(), fetchAllVerses()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  const actions = (
    <Button
      onClick={regenerateTodayQuran}
      loading={regenerating}
      disabled={loading}
      icon={<Icon name="spark" size={16} strokeWidth={2} />}
    >
      {regenerating ? "Genererer …" : "Generer dagens vers på nytt"}
    </Button>
  );

  if (loading) {
    return (
      <>
        {top(actions)}
        <DailySkeleton />
      </>
    );
  }

  return (
    <>
      {top(actions)}

      <DismissibleError message={error} onClose={() => setError(null)} />

      {todayQuran && (
        <DailyCard
          title="Dagens vers"
          badges={
            <>
              <Badge tone="grey">{formatShortDate(todayQuran.date_iso)}</Badge>
              <Badge tone="blue">{todayQuran.verse.source.surah_name}</Badge>
              <Badge tone="grey">
                Vers {todayQuran.verse.source.ayah_number}
              </Badge>
              {todayQuran.verse.source.reference_note && (
                <Badge tone="mint">
                  {todayQuran.verse.source.reference_note}
                </Badge>
              )}
            </>
          }
          meta={
            <>
              {todayQuran.cached ? "Fra cache" : "Nylig generert"}
              {!!todayQuran.confidence &&
                ` · Konfidens ${Math.round(todayQuran.confidence * 100)} %`}
              {` · Generert ${new Date(todayQuran.cacheTimestamp).toLocaleString("nb-NO")}`}
            </>
          }
          arabic={todayQuran.verse.arabic}
          transliteration={todayQuran.verse.transliteration}
          norwegian={todayQuran.verse.norwegian}
          explanation={todayQuran.explanation_norwegian}
          actionPoints={todayQuran.action_points}
          extra={
            todayQuran.fiqh_controversy_risk && (
              <div>
                <span className={styles.label}>Risiko for fiqh-uenighet</span>
                <p className={styles.text}>
                  {todayQuran.fiqh_controversy_risk}
                </p>
              </div>
            )
          }
          tags={todayQuran.topic_tags}
        />
      )}

      <DailyTvPoster kind="QURAN" refreshKey={todayQuran?.id} />

      <DailyHistory
        title={`Alle genererte vers (${allVerses.length})`}
        emptyIcon="quote"
        emptyTitle="Ingen vers ennå"
        emptyText="Trykk «Generer dagens vers på nytt» for å lage det første."
        rows={allVerses.map((item) => ({
          key: item.id,
          date: item.date_iso,
          text: item.verse.norwegian,
          arabic: item.verse.arabic,
          transliteration: item.verse.transliteration,
          confidence: item.confidence,
          source: `${item.verse.source.surah_name} (${item.verse.source.surah_number}:${item.verse.source.ayah_number})`,
        }))}
      />
    </>
  );
};

export default AiQuranTab;
